import React, { useMemo, useState } from "react";
import {
    BarChart3,
    Users,
    UserPlus,
    MessageSquare,
    CreditCard,
    IndianRupee,
    TrendingUp,
    Download,
    FileText,
    CalendarDays,
    Building2,
    Heart,
    CheckCircle,
    Clock,
    XCircle,
    ChevronDown,
} from "lucide-react";

/* =========================================================
   SAMPLE REPORT DATA
========================================================= */

const registrationData = [
    {
        month: "May",
        registrations: 72,
    },
    {
        month: "Jun",
        registrations: 95,
    },
    {
        month: "Jul",
        registrations: 110,
    },
    {
        month: "Aug",
        registrations: 126,
    },
    {
        month: "Sep",
        registrations: 142,
    },
    {
        month: "Oct",
        registrations: 86,
    },
];

const enquiryData = [
    {
        label: "New",
        value: 72,
        className: "report-bar-blue",
    },
    {
        label: "Contacted",
        value: 84,
        className: "report-bar-purple",
    },
    {
        label: "Follow-up",
        value: 65,
        className: "report-bar-orange",
    },
    {
        label: "Converted",
        value: 78,
        className: "report-bar-green",
    },
    {
        label: "Closed",
        value: 26,
        className: "report-bar-red",
    },
];

/* =========================================================
   BRANCH MEMBER + SUBSCRIPTION DATA
========================================================= */

const subscriptionBranches = [
    {
        name: "Erode Main Branch",
        totalMembers: 420,
        subscriptionMembers: 285,
        basic: 95,
        premium: 120,
        gold: 70,
        lastMonth: 250,
    },
    {
        name: "Coimbatore Branch",
        totalMembers: 315,
        subscriptionMembers: 210,
        basic: 72,
        premium: 86,
        gold: 52,
        lastMonth: 192,
    },
    {
        name: "Salem Branch",
        totalMembers: 245,
        subscriptionMembers: 165,
        basic: 58,
        premium: 65,
        gold: 42,
        lastMonth: 150,
    },
    {
        name: "Tiruppur Branch",
        totalMembers: 185,
        subscriptionMembers: 118,
        basic: 42,
        premium: 46,
        gold: 30,
        lastMonth: 105,
    },
    {
        name: "Namakkal Branch",
        totalMembers: 85,
        subscriptionMembers: 57,
        basic: 20,
        premium: 23,
        gold: 14,
        lastMonth: 48,
    },
];

/* =========================================================
   MAIN REPORT PAGE
========================================================= */

function Reports() {
    const [reportType, setReportType] =
        useState("All Reports");

    const [branch, setBranch] =
        useState("All Branches");

    const [fromDate, setFromDate] =
        useState("2026-10-01");

    const [toDate, setToDate] =
        useState("2026-10-05");

    const filteredBranches = useMemo(() => {
        if (branch === "All Branches") {
            return subscriptionBranches;
        }

        return subscriptionBranches.filter(
            (item) => item.name === branch
        );
    }, [branch]);

    const totalMembers =
        filteredBranches.reduce(
            (total, item) =>
                total + item.totalMembers,
            0
        );

    const totalSubscriptionMembers =
        filteredBranches.reduce(
            (total, item) =>
                total +
                item.subscriptionMembers,
            0
        );

    const totalBasic =
        filteredBranches.reduce(
            (total, item) =>
                total + item.basic,
            0
        );

    const totalPremium =
        filteredBranches.reduce(
            (total, item) =>
                total + item.premium,
            0
        );

    const totalGold =
        filteredBranches.reduce(
            (total, item) =>
                total + item.gold,
            0
        );

    const totalLastMonth =
        filteredBranches.reduce(
            (total, item) =>
                total + item.lastMonth,
            0
        );

    const overallSubscriptionGrowth =
        totalLastMonth > 0
            ? (
                  ((totalSubscriptionMembers -
                      totalLastMonth) /
                      totalLastMonth) *
                  100
              ).toFixed(1)
            : 0;

    const handleExport = (type) => {
        alert(
            `${type} export will be connected to the backend API.`
        );
    };

    return (
        <div className="reports-page">

            {/* =================================================
                PAGE HEADER
            ================================================= */}

            <div className="page-header">

                <div>
                    <h1>
                        Reports
                    </h1>

                    <p>
                        View and analyse
                        matrimonial business
                        performance
                    </p>
                </div>

                <div className="report-export-actions">

                    <button
                        className="report-export-btn secondary"
                        onClick={() =>
                            handleExport("Excel")
                        }
                    >
                        <Download size={16} />
                        Excel
                    </button>

                    <button
                        className="report-export-btn primary"
                        onClick={() =>
                            handleExport("PDF")
                        }
                    >
                        <FileText size={16} />
                        PDF
                    </button>

                </div>

            </div>

            {/* =================================================
                REPORT FILTER
            ================================================= */}

            <div className="report-filter-card">

                {/* REPORT TYPE */}

                <div className="report-filter-item">

                    <label>
                        Report Type
                    </label>

                    <div className="report-select">

                        <select
                            value={reportType}
                            onChange={(e) =>
                                setReportType(
                                    e.target.value
                                )
                            }
                        >

                            <option>
                                All Reports
                            </option>

                            <option>
                                Member Report
                            </option>

                            <option>
                                Registration Report
                            </option>

                            <option>
                                Enquiry Report
                            </option>

                            <option>
                                Membership Report
                            </option>

                            <option>
                                Payment Report
                            </option>

                            <option>
                                Subscription Report
                            </option>

                        </select>

                        <ChevronDown size={15} />

                    </div>

                </div>

                {/* BRANCH */}

                <div className="report-filter-item">

                    <label>
                        Branch
                    </label>

                    <div className="report-select">

                        <select
                            value={branch}
                            onChange={(e) =>
                                setBranch(
                                    e.target.value
                                )
                            }
                        >

                            <option>
                                All Branches
                            </option>

                            {subscriptionBranches.map(
                                (item) => (
                                    <option
                                        key={
                                            item.name
                                        }
                                    >
                                        {item.name}
                                    </option>
                                )
                            )}

                        </select>

                        <ChevronDown size={15} />

                    </div>

                </div>

                {/* FROM DATE */}

                <div className="report-filter-item">

                    <label>
                        From Date
                    </label>

                    <div className="report-date-input">

                        <CalendarDays
                            size={16}
                        />

                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) =>
                                setFromDate(
                                    e.target.value
                                )
                            }
                        />

                    </div>

                </div>

                {/* TO DATE */}

                <div className="report-filter-item">

                    <label>
                        To Date
                    </label>

                    <div className="report-date-input">

                        <CalendarDays
                            size={16}
                        />

                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) =>
                                setToDate(
                                    e.target.value
                                )
                            }
                        />

                    </div>

                </div>

                {/* APPLY */}

                <button
                    className="report-apply-btn"
                    onClick={() =>
                        alert(
                            "Report filters applied"
                        )
                    }
                >
                    Apply Report
                </button>

            </div>

            {/* =================================================
                SUMMARY CARDS
            ================================================= */}

            <div className="report-summary-grid">

                <ReportSummaryCard
                    title="Total Members"
                    value={totalMembers.toLocaleString(
                        "en-IN"
                    )}
                    subtitle="All registered members"
                    icon={
                        <Users size={21} />
                    }
                    className="blue"
                />

                <ReportSummaryCard
                    title="Subscription Members"
                    value={totalSubscriptionMembers.toLocaleString(
                        "en-IN"
                    )}
                    subtitle={`${overallSubscriptionGrowth}% vs last month`}
                    icon={
                        <UserPlus size={21} />
                    }
                    className="green"
                />

                <ReportSummaryCard
                    title="New Registrations"
                    value="86"
                    subtitle="This month"
                    icon={
                        <Users size={21} />
                    }
                    className="purple"
                />

                <ReportSummaryCard
                    title="Total Enquiries"
                    value="325"
                    subtitle="78 converted"
                    icon={
                        <MessageSquare
                            size={21}
                        />
                    }
                    className="orange"
                />

                <ReportSummaryCard
                    title="Total Payments"
                    value="985"
                    subtitle="932 successful"
                    icon={
                        <CreditCard
                            size={21}
                        />
                    }
                    className="pink"
                />

                <ReportSummaryCard
                    title="Total Revenue"
                    value="₹8,45,750"
                    subtitle="+12.5% growth"
                    icon={
                        <IndianRupee
                            size={21}
                        />
                    }
                    className="teal"
                />

            </div>

            {/* =================================================
                REGISTRATION + ENQUIRY
            ================================================= */}

            <div className="report-two-column">

                {/* REGISTRATION */}

                <div className="report-card">

                    <div className="report-card-header">

                        <div>

                            <h2>
                                Registration
                                Overview
                            </h2>

                            <span>
                                Monthly new
                                registrations
                            </span>

                        </div>

                        <TrendingUp
                            size={20}
                            className="report-header-icon"
                        />

                    </div>

                    <div className="registration-chart">

                        {registrationData.map(
                            (item) => (
                                <div
                                    className="registration-column"
                                    key={
                                        item.month
                                    }
                                >

                                    <span className="registration-value">
                                        {
                                            item.registrations
                                        }
                                    </span>

                                    <div className="registration-bar-wrapper">

                                        <div
                                            className="registration-bar"
                                            style={{
                                                height: `${Math.min(
                                                    Math.max(
                                                        item.registrations *
                                                            1.15,
                                                        25
                                                    ),
                                                    180
                                                )}px`,
                                            }}
                                        />

                                    </div>

                                    <span className="registration-month">
                                        {item.month}
                                    </span>

                                </div>
                            )
                        )}

                    </div>

                </div>

                {/* ENQUIRY */}

                <div className="report-card">

                    <div className="report-card-header">

                        <div>

                            <h2>
                                Enquiry Overview
                            </h2>

                            <span>
                                Current enquiry
                                status
                            </span>

                        </div>

                        <MessageSquare
                            size={20}
                            className="report-header-icon"
                        />

                    </div>

                    <div className="enquiry-report-bars">

                        {enquiryData.map(
                            (item) => (
                                <div
                                    className="enquiry-report-row"
                                    key={
                                        item.label
                                    }
                                >

                                    <div className="enquiry-report-label">

                                        <span>
                                            {
                                                item.label
                                            }
                                        </span>

                                        <strong>
                                            {
                                                item.value
                                            }
                                        </strong>

                                    </div>

                                    <div className="enquiry-report-track">

                                        <div
                                            className={`enquiry-report-progress ${item.className}`}
                                            style={{
                                                width: `${Math.min(
                                                    item.value,
                                                    100
                                                )}%`,
                                            }}
                                        />

                                    </div>

                                </div>
                            )
                        )}

                    </div>

                </div>

            </div>

            {/* =================================================
                PAYMENT + MEMBERSHIP
            ================================================= */}

            <div className="report-two-column">

                {/* PAYMENT */}

                <div className="report-card">

                    <div className="report-card-header">

                        <div>

                            <h2>
                                Payment Overview
                            </h2>

                            <span>
                                Payment transaction
                                summary
                            </span>

                        </div>

                        <CreditCard
                            size={20}
                            className="report-header-icon"
                        />

                    </div>

                    <div className="payment-report-grid">

                        <PaymentStat
                            label="Successful"
                            value="932"
                            icon={
                                <CheckCircle
                                    size={17}
                                />
                            }
                            className="success"
                        />

                        <PaymentStat
                            label="Pending"
                            value="21"
                            icon={
                                <Clock
                                    size={17}
                                />
                            }
                            className="pending"
                        />

                        <PaymentStat
                            label="Failed"
                            value="32"
                            icon={
                                <XCircle
                                    size={17}
                                />
                            }
                            className="failed"
                        />

                        <PaymentStat
                            label="Revenue"
                            value="₹8,45,750"
                            icon={
                                <IndianRupee
                                    size={17}
                                />
                            }
                            className="revenue"
                        />

                    </div>

                </div>

                {/* MEMBERSHIP */}

                <div className="report-card">

                    <div className="report-card-header">

                        <div>

                            <h2>
                                Membership
                                Overview
                            </h2>

                            <span>
                                Plan-wise active
                                subscriptions
                            </span>

                        </div>

                        <Heart
                            size={20}
                            className="report-header-icon"
                        />

                    </div>

                    <div className="membership-report-list">

                        <MembershipRow
                            plan="Basic"
                            members={totalBasic}
                            revenue="₹1,68,000"
                            percentage="32%"
                        />

                        <MembershipRow
                            plan="Premium"
                            members={totalPremium}
                            revenue="₹2,92,000"
                            percentage="42%"
                        />

                        <MembershipRow
                            plan="Gold"
                            members={totalGold}
                            revenue="₹3,85,750"
                            percentage="48%"
                        />

                    </div>

                </div>

            </div>

            {/* =================================================
                MEMBER & SUBSCRIPTION PERFORMANCE
            ================================================= */}

            <div className="report-card member-subscription-card">

                <div className="report-card-header">

                    <div>

                        <h2>
                            Member & Subscription
                            Performance
                        </h2>

                        <span>
                            Branch-wise member and
                            subscription comparison
                        </span>

                    </div>

                    <Users
                        size={20}
                        className="report-header-icon"
                    />

                </div>

                {/* SUMMARY */}

                <div className="subscription-overall-summary">

                    <div className="subscription-overall-item">

                        <span>
                            Total Members
                        </span>

                        <strong>
                            {totalMembers.toLocaleString(
                                "en-IN"
                            )}
                        </strong>

                    </div>

                    <div className="subscription-overall-item">

                        <span>
                            Subscription Members
                        </span>

                        <strong>
                            {totalSubscriptionMembers.toLocaleString(
                                "en-IN"
                            )}
                        </strong>

                    </div>

                    <div className="subscription-overall-item">

                        <span>
                            Basic Subscriptions
                        </span>

                        <strong>
                            {totalBasic.toLocaleString(
                                "en-IN"
                            )}
                        </strong>

                    </div>

                    <div className="subscription-overall-item">

                        <span>
                            Premium Subscriptions
                        </span>

                        <strong>
                            {totalPremium.toLocaleString(
                                "en-IN"
                            )}
                        </strong>

                    </div>

                    <div className="subscription-overall-item">

                        <span>
                            Gold Subscriptions
                        </span>

                        <strong>
                            {totalGold.toLocaleString(
                                "en-IN"
                            )}
                        </strong>

                    </div>

                    <div className="subscription-overall-item growth">

                        <span>
                            Compared to Last Month
                        </span>

                        <strong>
                            ↑{" "}
                            {
                                overallSubscriptionGrowth
                            }
                            %
                        </strong>

                    </div>

                </div>

                {/* BRANCH TABLE */}

                <div className="report-table-wrapper">

                    <table className="report-table">

                        <thead>

                            <tr>

                                <th>
                                    S.No
                                </th>

                                <th>
                                    Branch
                                </th>

                                <th>
                                    Total Members
                                </th>

                                <th>
                                    Subscription Members
                                </th>

                                <th>
                                    Basic
                                </th>

                                <th>
                                    Premium
                                </th>

                                <th>
                                    Gold
                                </th>

                                <th>
                                    Last Month
                                </th>

                                <th>
                                    Growth
                                </th>

                            </tr>

                        </thead>

                        <tbody>

                            {filteredBranches.map(
                                (
                                    item,
                                    index
                                ) => {

                                    const growth =
                                        item.lastMonth >
                                        0
                                            ? (
                                                  ((item.subscriptionMembers -
                                                      item.lastMonth) /
                                                      item.lastMonth) *
                                                  100
                                              ).toFixed(
                                                  1
                                              )
                                            : 0;

                                    return (
                                        <tr
                                            key={
                                                item.name
                                            }
                                        >

                                            <td>
                                                {index +
                                                    1}
                                            </td>

                                            <td>

                                                <div className="branch-report-name">

                                                    <div className="branch-report-icon">
                                                        <Building2
                                                            size={
                                                                16
                                                            }
                                                        />
                                                    </div>

                                                    <strong>
                                                        {
                                                            item.name
                                                        }
                                                    </strong>

                                                </div>

                                            </td>

                                            <td>

                                                <strong>
                                                    {item.totalMembers.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </strong>

                                            </td>

                                            <td>

                                                <span className="subscription-member-badge">
                                                    {item.subscriptionMembers.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </span>

                                            </td>

                                            <td>
                                                {
                                                    item.basic
                                                }
                                            </td>

                                            <td>
                                                {
                                                    item.premium
                                                }
                                            </td>

                                            <td>
                                                {
                                                    item.gold
                                                }
                                            </td>

                                            <td>
                                                {
                                                    item.lastMonth
                                                }
                                            </td>

                                            <td>

                                                <span className="growth-badge">
                                                    ↑{" "}
                                                    {
                                                        growth
                                                    }
                                                    %
                                                </span>

                                            </td>

                                        </tr>
                                    );
                                }
                            )}

                        </tbody>

                    </table>

                </div>

                {/* PLAN COMPARISON */}

                <div className="subscription-comparison">

                    <div className="subscription-comparison-header">

                        <div>

                            <h3>
                                Plan-wise Subscription
                                Comparison
                            </h3>

                            <span>
                                Current month vs
                                last month
                            </span>

                        </div>

                        <TrendingUp
                            size={19}
                            className="report-header-icon"
                        />

                    </div>

                    <div className="subscription-plan-grid">

                        <PlanComparison
                            plan="Basic"
                            current={totalBasic}
                            previous={250}
                        />

                        <PlanComparison
                            plan="Premium"
                            current={totalPremium}
                            previous={295}
                        />

                        <PlanComparison
                            plan="Gold"
                            current={totalGold}
                            previous={178}
                        />

                    </div>

                </div>

            </div>

        </div>
    );
}

/* =========================================================
   SUMMARY CARD
========================================================= */

function ReportSummaryCard({
    title,
    value,
    subtitle,
    icon,
    className,
}) {
    return (
        <div className="report-summary-card">

            <div
                className={`report-summary-icon ${className}`}
            >
                {icon}
            </div>

            <div className="report-summary-content">

                <span>
                    {title}
                </span>

                <strong>
                    {value}
                </strong>

                <small>
                    {subtitle}
                </small>

            </div>

        </div>
    );
}

/* =========================================================
   PAYMENT STAT
========================================================= */

function PaymentStat({
    label,
    value,
    icon,
    className,
}) {
    return (
        <div
            className={`payment-stat ${className}`}
        >

            <div className="payment-stat-icon">
                {icon}
            </div>

            <div>

                <span>
                    {label}
                </span>

                <strong>
                    {value}
                </strong>

            </div>

        </div>
    );
}

/* =========================================================
   MEMBERSHIP ROW
========================================================= */

function MembershipRow({
    plan,
    members,
    revenue,
    percentage,
}) {
    return (
        <div className="membership-report-row">

            <div className="membership-plan-name">

                <div className="membership-dot" />

                <strong>
                    {plan}
                </strong>

            </div>

            <span>
                {members} members
            </span>

            <strong>
                {revenue}
            </strong>

            <span className="membership-percentage">
                {percentage}
            </span>

        </div>
    );
}

/* =========================================================
   PLAN COMPARISON
========================================================= */

function PlanComparison({
    plan,
    current,
    previous,
}) {
    const growth =
        previous > 0
            ? (
                  ((current - previous) /
                      previous) *
                  100
              ).toFixed(1)
            : 0;

    return (
        <div className="plan-comparison-card">

            <div className="plan-comparison-top">

                <div className="plan-comparison-name">

                    <div className="membership-dot" />

                    <strong>
                        {plan}
                    </strong>

                </div>

                <span
                    className={
                        Number(growth) >= 0
                            ? "growth-badge"
                            : "decline-badge"
                    }
                >
                    {Number(growth) >= 0
                        ? "↑"
                        : "↓"}{" "}
                    {Math.abs(growth)}%
                </span>

            </div>

            <div className="plan-current">

                <strong>
                    {current.toLocaleString(
                        "en-IN"
                    )}
                </strong>

                <span>
                    subscriptions
                </span>

            </div>

            <div className="plan-previous">

                <span>
                    Last Month
                </span>

                <strong>
                    {previous.toLocaleString(
                        "en-IN"
                    )}
                </strong>

            </div>

        </div>
    );
}

export default Reports;